import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Truck, PackageCheck, MapPin } from "lucide-react";

export const VEHICLE_STATUSES = ["AVAILABLE", "ASSIGNED", "LOADING", "FULL", "DEPARTED", "AT_PASHU_HAAT", "COMPLETED", "OFFLINE"];

const BADGE = {
  AVAILABLE: "bg-emerald-100 text-emerald-800",
  ASSIGNED: "bg-blue-100 text-blue-800",
  LOADING: "bg-amber-100 text-amber-900",
  FULL: "bg-orange-200 text-orange-900",
  DEPARTED: "bg-indigo-100 text-indigo-800",
  AT_PASHU_HAAT: "bg-purple-100 text-purple-800",
  COMPLETED: "bg-slate-200 text-slate-700",
  OFFLINE: "bg-slate-100 text-slate-500",
  PENDING: "bg-amber-100 text-amber-900",
  MATCHING: "bg-blue-100 text-blue-800",
  CANCELLED: "bg-red-100 text-red-700",
};

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${BADGE[status] || "bg-muted"}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

export function KpiGrid({ data, compact }) {
  if (!data) return null;
  const cards = [
    ["Total Vehicles", data.total_vehicles],
    ["Available", data.by_status.AVAILABLE],
    ["Assigned", data.by_status.ASSIGNED],
    ["Loading", data.by_status.LOADING],
    ["Full", data.by_status.FULL],
    ["Departed", data.by_status.DEPARTED],
    ["At Pashu Haat", data.by_status.AT_PASHU_HAAT],
    ["Completed", data.by_status.COMPLETED],
    ...(compact ? [] : [["Total Trips", data.total_trips], ["Total Fare", `₹${(data.total_fare || 0).toLocaleString("en-IN")}`]]),
  ];
  return (
    <div className={`grid gap-3 ${compact ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"}`} data-testid="tr-kpi-grid">
      {cards.map(([label, value]) => (
        <div key={label} className="am-card p-4" data-testid={`tr-kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
          <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
          <div className="font-display text-2xl font-extrabold mt-1">{value}</div>
        </div>
      ))}
    </div>
  );
}

/** Helper operational buttons for a vehicle, by current status. */
export function VehicleOpsButtons({ vehicle, onDone }) {
  const [busy, setBusy] = useState(false);
  const [countOpen, setCountOpen] = useState(false);
  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/transport/vehicles/${vehicle.vehicle_id}/status`, { status });
      toast.success(`${vehicle.registration_number}: ${status.replace(/_/g, " ")}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const btn = (label, status, style = "outline") => (
    <Button key={status} size="sm" variant={style} disabled={busy} className="rounded-full text-xs"
      data-testid={`tr-op-${status.toLowerCase()}-${vehicle.vehicle_id}`} onClick={() => setStatus(status)}>
      {label}
    </Button>
  );
  const s = vehicle.status;
  return (
    <div className="flex flex-wrap gap-1.5 justify-end">
      {s === "ASSIGNED" && btn("Start Loading", "LOADING", "default")}
      {(s === "LOADING" || s === "ASSIGNED") && (
        <Button size="sm" variant="outline" disabled={busy} className="rounded-full text-xs"
          data-testid={`tr-op-count-${vehicle.vehicle_id}`} onClick={() => setCountOpen(true)}>
          Update Count
        </Button>
      )}
      {s === "LOADING" && vehicle.loaded_animals < vehicle.animal_capacity && btn("Mark Full", "FULL")}
      {(s === "LOADING" || s === "FULL") && btn("Departed", "DEPARTED")}
      {s === "DEPARTED" && btn("At Pashu Haat", "AT_PASHU_HAAT")}
      {s === "AT_PASHU_HAAT" && btn("Complete Trip", "COMPLETED", "default")}
      {(s === "COMPLETED" || s === "OFFLINE") && btn("Set Available", "AVAILABLE")}
      <CountDialog vehicle={vehicle} open={countOpen} setOpen={setCountOpen} onDone={onDone} />
    </div>
  );
}

/** Absolute animal-count updater with live remaining preview. */
export function CountDialog({ vehicle, open, setOpen, onDone }) {
  const [count, setCount] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setCount(String(vehicle.loaded_animals)); }, [open, vehicle.loaded_animals]);
  const n = parseInt(count, 10);
  const valid = !isNaN(n) && n >= 0 && n <= vehicle.animal_capacity;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/transport/vehicles/${vehicle.vehicle_id}/set-loaded`, { count: n });
      toast.success(`Loaded ${r.data.loaded_animals} / remaining ${r.data.remaining_capacity}`);
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm" data-testid="tr-count-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PackageCheck className="w-5 h-5 text-primary" /> Update Animal Count</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {vehicle.registration_number} — capacity <b>{vehicle.animal_capacity}</b>, currently loaded <b>{vehicle.loaded_animals}</b>
        </p>
        <Input type="number" min={0} max={vehicle.animal_capacity} value={count} inputMode="numeric"
          className="am-input text-lg font-bold" data-testid="tr-count-input"
          onChange={(e) => setCount(e.target.value)} />
        <p className={`text-sm font-semibold ${valid ? "text-muted-foreground" : "text-destructive"}`} data-testid="tr-count-remaining">
          {valid ? `Remaining capacity will be ${vehicle.animal_capacity - n}` : `Must be between 0 and ${vehicle.animal_capacity}`}
        </p>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="am-btn-primary" disabled={!valid || busy} data-testid="tr-count-save" onClick={save}>Save Count</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Assign / reassign an eligible vehicle to a transport request. */
export function AssignDialog({ request, open, setOpen, onDone }) {
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !request) return;
    setMatches(null);
    api.get(`/transport/requests/${request.request_id}/matches`)
      .then((r) => setMatches(r.data.eligible_vehicles))
      .catch(() => setMatches([]));
  }, [open, request]);
  const assign = async (vehicleId) => {
    setBusy(true);
    try {
      await api.post(`/transport/requests/${request.request_id}/assign`, { vehicle_id: vehicleId });
      toast.success("Vehicle assigned");
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assignment failed"); }
    finally { setBusy(false); }
  };
  if (!request) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="tr-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-primary" /> Assign Vehicle</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
          <MapPin className="w-4 h-4 shrink-0" />
          {request.pickup_area} → {request.destination_pashu_haat} · {request.animal_quantity} {request.animal_type} (needs {request.required_capacity})
        </p>
        {!matches && <p className="text-sm text-muted-foreground py-4 text-center">Finding eligible vehicles…</p>}
        {matches && matches.length === 0 && (
          <p className="text-sm font-semibold text-destructive py-4 text-center" data-testid="tr-no-matches">
            No eligible vehicles (need: AVAILABLE, active, area match, enough capacity, driver available)
          </p>
        )}
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {(matches || []).map((v) => (
            <div key={v.vehicle_id} className="rounded-xl border border-border p-3 flex items-center gap-3" data-testid={`tr-match-${v.vehicle_id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm">{v.registration_number} <span className="font-normal text-muted-foreground">· {v.vehicle_type}</span></div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {v.operating_area} ({v.area_match === "exact" ? "exact area" : "transporter area"}) · free capacity {v.remaining_capacity} · driver {v.driver?.name}
                </div>
              </div>
              <Button size="sm" className="am-btn-primary rounded-full shrink-0" disabled={busy}
                data-testid={`tr-assign-${v.vehicle_id}`} onClick={() => assign(v.vehicle_id)}>
                {request.assigned_vehicle_id ? "Reassign" : "Assign"}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TabBtn({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 border transition-all whitespace-nowrap ${active ? "bg-primary text-primary-foreground border-primary" : "bg-transparent hover:bg-accent border-border"}`}>
      {Icon && <Icon className="w-4 h-4" />} {children}
    </button>
  );
}

export const fmtMoney = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;
